import type { Catalogs, Translation } from "@amtana/i18n/catalog";

// The back office's words for the funnel report (ADR-028 point 6, revision 1 points 3 and 4,
// S33.04). `ops-copy.ts` puts them together with the rest into the one catalogue the screens read.

export type FunnelCopyCatalog = typeof EN;

const EN = {
  titleFunnel: "Funnel · Back office · {product}",
  funnelPageTitle: "Funnel",
  funnelPageDescription:
    "Reach, visit, signup, activate and convert for the last 28 full days. Each number comes from the module that owns it.",
  funnelWindow: "Last 28 days · {from} to {to} (UTC) · against the 28 before",
  funnelSourceLabel: "Source",
  funnelSourceAll: "All sources",
  funnelLanguageLabel: "Language",
  funnelLanguageAll: "All",
  funnelLanguageEn: "EN",
  funnelLanguageEs: "ES",
  funnelLanguageOther: "Other",
  funnelSourceDirect: "Direct",
  funnelSourceOther: "Other",
  funnelChartLabel: "The funnel for the chosen source and language",
  funnelScale: "log",
  funnelStepHeading: "Step rate",
  funnelBoundary: {
    one: "Join boundary · above: separate totals · below: the same person",
    other: "Join boundary · above: separate totals · below: the same {count} people",
  },
  stageReach: "Reach",
  stageReachCaption: "Google impressions",
  reachDetail: "{clicks} clicks · CTR {ctr}",
  reachNotStored: "No Search Console day stored yet",
  reachGoogleOnly: "Search Console counts Google only",
  stageVisit: "Visit",
  stageVisitCaption: "Visitors who allowed analytics",
  visitDetailClicks: "{clicks} Google clicks beside {visits} visits from Google, never divided",
  visitDetailGoogle: "{visits} visits from Google",
  visitUnavailable: "PostHog did not answer",
  stageSignup: "Signup",
  stageSignupCaption: "Every new account",
  signupLinked: "{linked} linked to a consented visit",
  stageActivate: "Activate",
  stageActivateCaption: "First completed analysis",
  stageConvert: "Convert",
  stageConvertCaption: "First payment over zero",
  changeAgainstPrior: "{change} vs prior",
  changeFromNone: "New, none before",
  stepNotJoined: "Not joined, no rate",
  stepReachToVisit: "Impressions are not people",
  stepNoLink: "No signup carries a consented visit yet",
  stepVisitUnavailable: "PostHog did not answer",
  stepOfVisitors: "of consented visitors",
  stepLinkedFraction: "{linked} linked / {visits}",
  stepNoConsentedVisit: {
    one: "1 signup had no consented visit: direct, another device, or no consent",
    other: "{count} signups had no consented visit: direct, another device, or no consent",
  },
  stepOfSignups: "of signups",
  stepOfActivated: "of activated",
  stepSignupToConvert: "Signup to convert: {rate}",
  stepHint: "{rate}: {numerator} of {denominator}",
  tileReturning: "Returning",
  tileReturningHint: "Signups seen in the last 7 days",
  tileStuck: "Stuck",
  tileStuckHint: "Spent credits, no completed analysis",
  tileNoConsentedVisit: "No consented visit",
  tileNoConsentedVisitHint: "Signed up with no visit PostHog saw",
  tileOf: "{part} of {total}",
  funnelBySource: "By source",
  funnelByLanguage: "By language",
  columnFunnelSource: "Source",
  columnFunnelLanguage: "Language",
  columnFunnelVisit: "Visit",
  columnFunnelSignup: "Signup",
  columnFunnelLinked: "Linked",
  columnFunnelActivate: "Activate",
  columnFunnelConvert: "Convert",
  columnFunnelSignupToActivate: "Signup to activate",
  funnelNotRead: "Not read",
  funnelNoRate: "No rate",
  funnelCalloutTitle: "Visitors who did not consent",
  funnelCalloutDescription:
    "They are never in Visit. They still count in Reach as clicks and in Signup as accounts. The report never divides two totals that are not joined.",
} as const;

const ES: Translation<FunnelCopyCatalog> = {
  titleFunnel: "Embudo · Back office · {product}",
  funnelPageTitle: "Embudo",
  funnelPageDescription:
    "Alcance, visita, registro, activación y conversión de los últimos 28 días completos. Cada número viene del módulo dueño del dato.",
  funnelWindow: "Últimos 28 días · del {from} al {to} (UTC) · frente a los 28 anteriores",
  funnelSourceLabel: "Origen",
  funnelSourceAll: "Todos los orígenes",
  funnelLanguageLabel: "Idioma",
  funnelLanguageAll: "Todos",
  funnelLanguageEn: "EN",
  funnelLanguageEs: "ES",
  funnelLanguageOther: "Otro",
  funnelSourceDirect: "Directo",
  funnelSourceOther: "Otros",
  funnelChartLabel: "El embudo para el origen y el idioma elegidos",
  funnelScale: "log",
  funnelStepHeading: "Tasa del paso",
  funnelBoundary: {
    one: "Límite de unión · arriba: totales separados · abajo: la misma persona",
    other: "Límite de unión · arriba: totales separados · abajo: las mismas {count} personas",
  },
  stageReach: "Alcance",
  stageReachCaption: "Impresiones en Google",
  reachDetail: "{clicks} clics · CTR {ctr}",
  reachNotStored: "Aún no hay ningún día de Search Console guardado",
  reachGoogleOnly: "Search Console cuenta solo Google",
  stageVisit: "Visita",
  stageVisitCaption: "Visitantes que permitieron la analítica",
  visitDetailClicks: "{clicks} clics de Google junto a {visits} visitas desde Google, nunca divididos",
  visitDetailGoogle: "{visits} visitas desde Google",
  visitUnavailable: "PostHog no respondió",
  stageSignup: "Registro",
  stageSignupCaption: "Toda cuenta nueva",
  signupLinked: "{linked} unidos a una visita con consentimiento",
  stageActivate: "Activación",
  stageActivateCaption: "Primer análisis terminado",
  stageConvert: "Conversión",
  stageConvertCaption: "Primer pago mayor que cero",
  changeAgainstPrior: "{change} frente al anterior",
  changeFromNone: "Nuevo, ninguno antes",
  stepNotJoined: "Sin unir, sin tasa",
  stepReachToVisit: "Las impresiones no son personas",
  stepNoLink: "Ningún registro trae todavía una visita con consentimiento",
  stepVisitUnavailable: "PostHog no respondió",
  stepOfVisitors: "de los visitantes con consentimiento",
  stepLinkedFraction: "{linked} unidos / {visits}",
  stepNoConsentedVisit: {
    one: "1 registro no tuvo visita con consentimiento: directo, otro dispositivo o sin consentimiento",
    other:
      "{count} registros no tuvieron visita con consentimiento: directo, otro dispositivo o sin consentimiento",
  },
  stepOfSignups: "de los registros",
  stepOfActivated: "de los activados",
  stepSignupToConvert: "Del registro a la conversión: {rate}",
  stepHint: "{rate}: {numerator} de {denominator}",
  tileReturning: "Vuelven",
  tileReturningHint: "Registros vistos en los últimos 7 días",
  tileStuck: "Atascados",
  tileStuckHint: "Gastaron créditos, sin análisis terminado",
  tileNoConsentedVisit: "Sin visita con consentimiento",
  tileNoConsentedVisitHint: "Se registraron sin una visita que PostHog viera",
  tileOf: "{part} de {total}",
  funnelBySource: "Por origen",
  funnelByLanguage: "Por idioma",
  columnFunnelSource: "Origen",
  columnFunnelLanguage: "Idioma",
  columnFunnelVisit: "Visita",
  columnFunnelSignup: "Registro",
  columnFunnelLinked: "Unidos",
  columnFunnelActivate: "Activación",
  columnFunnelConvert: "Conversión",
  columnFunnelSignupToActivate: "Del registro a la activación",
  funnelNotRead: "Sin leer",
  funnelNoRate: "Sin tasa",
  funnelCalloutTitle: "Visitantes que no dieron su consentimiento",
  funnelCalloutDescription:
    "Nunca están en Visita. Siguen contando en Alcance como clics y en Registro como cuentas. El informe nunca divide dos totales que no están unidos.",
};

export const FUNNEL_CATALOGS: Catalogs<FunnelCopyCatalog> = { en: EN, es: ES };
